import { DEFAULT_ENABLED_LAYER_IDS } from "./map-signal-contract.js";
import {
  DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  TRADE_NPC_LAYER_ID,
  normalizeTradeNpcClass,
  normalizeVisibleTradeNpcClasses,
} from "./map-trade-npc-contract.js";
import { buildFocusWorldPointSignalPatch } from "./map-selection-actions.js";
import { tradeNpcKeyFromSearchTerm } from "./map-search-contract.js";
import {
  tradeNpcFocusTargetForSelectors,
  tradeNpcForKey,
} from "./map-trade-summary.js";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function tradeNpcKeysFromExpression(expression) {
  const all = new Set();
  const positive = new Set();
  const visit = (node, ancestorNegated = false) => {
    if (!isPlainObject(node)) {
      return;
    }
    const negated = ancestorNegated !== (node.negated === true);
    if (node.type === "term") {
      const npcKey = tradeNpcKeyFromSearchTerm(node.term);
      if (npcKey != null) {
        all.add(npcKey);
        if (!negated) {
          positive.add(npcKey);
        }
      }
      return;
    }
    if (node.type === "container") {
      visit(node.expression, negated);
      return;
    }
    for (const child of Array.isArray(node.children) ? node.children : []) {
      visit(child, negated);
    }
  };
  visit(expression);
  return { all, positive };
}

function normalizedVisibleLayerIds(signals) {
  const source = Array.isArray(signals?._map_bridged?.filters?.layerIdsVisible)
    ? signals._map_bridged.filters.layerIdsVisible
    : DEFAULT_ENABLED_LAYER_IDS;
  return Array.from(
    new Set(source.map((layerId) => String(layerId ?? "").trim()).filter(Boolean)),
  );
}

function normalizedVisibleTradeNpcClasses(signals) {
  return normalizeVisibleTradeNpcClasses(
    signals?._map_bridged?.filters?.tradeNpcClassesVisible
      ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  );
}

function rawTradeNpcCatalog(catalog) {
  if (Array.isArray(catalog)) {
    return { features: catalog };
  }
  return isPlainObject(catalog) ? catalog : { features: [] };
}

export function augmentTradeNpcQueryActivationPatch(
  patch,
  {
    signals = {},
    previousExpression = null,
    nextExpression = null,
    tradeNpcCatalog = null,
  } = {},
) {
  if (!isPlainObject(patch)) {
    return patch;
  }
  const previousNpcKeys = tradeNpcKeysFromExpression(previousExpression);
  const nextNpcKeys = tradeNpcKeysFromExpression(nextExpression);
  const enteringNpcKeys = Array.from(nextNpcKeys.all)
    .filter((npcKey) => !previousNpcKeys.all.has(npcKey));
  const enteringPositiveNpcKeys = Array.from(nextNpcKeys.positive)
    .filter((npcKey) => !previousNpcKeys.positive.has(npcKey));
  if (!enteringNpcKeys.length && !enteringPositiveNpcKeys.length) {
    return patch;
  }

  const nextPatch = cloneJson(patch);
  const visibleLayerIds = normalizedVisibleLayerIds(signals);
  const rawCatalog = rawTradeNpcCatalog(tradeNpcCatalog);
  const enteringLayerIds = enteringNpcKeys.length ? [TRADE_NPC_LAYER_ID] : [];
  const nextVisibleLayerIds = [
    ...visibleLayerIds,
    ...enteringLayerIds.filter((layerId) => !visibleLayerIds.includes(layerId)),
  ];
  const visibleClasses = normalizedVisibleTradeNpcClasses(signals);
  const enteringClasses = Array.from(new Set(
    enteringNpcKeys
      .map((npcKey) => normalizeTradeNpcClass(tradeNpcForKey(rawCatalog, npcKey)?.class))
      .filter(Boolean),
  ));
  const nextVisibleClasses = [
    ...visibleClasses,
    ...enteringClasses.filter((classId) => !visibleClasses.includes(classId)),
  ];
  if (
    nextVisibleLayerIds.length !== visibleLayerIds.length
    || nextVisibleClasses.length !== visibleClasses.length
  ) {
    nextPatch._map_bridged = {
      ...(isPlainObject(nextPatch._map_bridged) ? nextPatch._map_bridged : {}),
      filters: {
        ...(isPlainObject(nextPatch._map_bridged?.filters)
          ? nextPatch._map_bridged.filters
          : {}),
        ...(nextVisibleLayerIds.length !== visibleLayerIds.length
          ? { layerIdsVisible: nextVisibleLayerIds }
          : {}),
        ...(nextVisibleClasses.length !== visibleClasses.length
          ? { tradeNpcClassesVisible: nextVisibleClasses }
          : {}),
      },
    };
  }

  if (enteringPositiveNpcKeys.length !== 1) {
    return nextPatch;
  }
  const focusWorldPoint = tradeNpcFocusTargetForSelectors(
    [String(enteringPositiveNpcKeys[0])],
    rawCatalog,
  );
  const focusPatch = buildFocusWorldPointSignalPatch(focusWorldPoint, signals);
  if (!focusPatch) {
    return nextPatch;
  }
  if (isPlainObject(focusPatch._map_actions)) {
    nextPatch._map_actions = cloneJson(focusPatch._map_actions);
  }
  if (isPlainObject(focusPatch._map_session)) {
    nextPatch._map_session = cloneJson(focusPatch._map_session);
  }
  return nextPatch;
}
